import SignaturePad, { type PointGroup } from 'signature_pad';
import { findInkBounds } from './signature-strokes';

/**
 * Ink and background of the pad: black strokes on a transparent background,
 * so the exported PNG has no background on the contracts.
 */
export const SIGNATURE_PAD_PEN = {
  penColor: '#000000',
  backgroundColor: 'rgba(0, 0, 0, 0)',
} as const;

/** The exported image is twice the pad's CSS size, to stay sharp in print. */
export const SIGNATURE_EXPORT_SCALE = 2;

const SIGNATURE_FILE_NAME = 'signature.png';
const SIGNATURE_MIME_TYPE = 'image/png';

/**
 * Creates a transparent canvas of the given CSS size at the given pixel ratio,
 * its context scaled so strokes are drawn in CSS pixels.
 *
 * @param params.width - Width in CSS pixels.
 * @param params.height - Height in CSS pixels.
 * @param params.scale - Device pixels per CSS pixel.
 * @returns The scaled canvas.
 */
function createScaledCanvas({
  width,
  height,
  scale,
}: {
  width: number;
  height: number;
  scale: number;
}): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext('2d')?.scale(scale, scale);
  return canvas;
}

/**
 * Encodes a canvas as a PNG blob.
 *
 * @param canvas - The canvas to encode.
 * @returns The PNG blob.
 * @throws Error when the browser cannot encode the canvas.
 */
function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error('signature_png_encoding_failed'));
    }, SIGNATURE_MIME_TYPE);
  });
}

/**
 * Renders the pad's strokes into a PNG file: transparent background, black
 * ink, {@link SIGNATURE_EXPORT_SCALE}× the pad's CSS size whatever the
 * device's pixel ratio, trimmed to the ink so the signature renders the same
 * on the documents wherever it was drawn on the pad.
 *
 * @param params.pointGroups - The pad's strokes (`SignaturePad#toData()`).
 * @param params.width - Width of the pad in CSS pixels.
 * @param params.height - Height of the pad in CSS pixels.
 * @returns The `signature.png` file to upload.
 * @throws Error when the strokes leave no ink or the canvas cannot be read.
 */
export async function exportSignaturePng({
  pointGroups,
  width,
  height,
}: {
  pointGroups: PointGroup[];
  width: number;
  height: number;
}): Promise<File> {
  const rendering = createScaledCanvas({
    width,
    height,
    scale: SIGNATURE_EXPORT_SCALE,
  });
  const offscreenPad = new SignaturePad(rendering, SIGNATURE_PAD_PEN);
  offscreenPad.fromData(pointGroups);
  offscreenPad.off();

  const context = rendering.getContext('2d');
  if (!context) {
    throw new Error('signature_canvas_unavailable');
  }
  const rendered = context.getImageData(
    0,
    0,
    rendering.width,
    rendering.height,
  );
  const bounds = findInkBounds({
    pixels: rendered.data,
    width: rendered.width,
    height: rendered.height,
  });
  if (!bounds) {
    throw new Error('signature_without_ink');
  }

  const trimmed = document.createElement('canvas');
  trimmed.width = bounds.width;
  trimmed.height = bounds.height;
  const ink = context.getImageData(
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height,
  );
  trimmed.getContext('2d')?.putImageData(ink, 0, 0);

  const blob = await canvasToPngBlob(trimmed);
  return new File([blob], SIGNATURE_FILE_NAME, { type: SIGNATURE_MIME_TYPE });
}
